import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuthState } from "@/hooks/useAuthState";

const Privacy = () => {
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar 
        isAuthenticated={isAuthenticated} 
        isOwner={userType === "owner"} 
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      <main className="flex-1 container mx-auto px-4 py-12">
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-3xl md:text-4xl font-bold text-center">
              Política de Privacidade
            </CardTitle>
            <p className="text-center text-muted-foreground mt-2">
              Última atualização: 14 de outubro de 2025
            </p>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-semibold mb-3">1. Introdução</h2>
              <p className="text-muted-foreground">
                A Live Stay respeita sua privacidade e está comprometida em proteger seus dados
                pessoais. Esta política de privacidade explica como coletamos, usamos, armazenamos
                e protegemos suas informações quando você usa nossa plataforma, em conformidade com 
                a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">2. Informações que Coletamos</h2>
              <p className="text-muted-foreground mb-2">Coletamos os seguintes tipos de informações:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold mb-2">Informações Fornecidas por Você:</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Dados de cadastro: nome, email, telefone</li>
                  <li>Dados de perfil: foto, preferências, histórico de reservas</li>
                  <li>Comunicações: mensagens trocadas com proprietários ou suporte</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">3. Como Usamos suas Informações</h2>
              <p className="text-muted-foreground mb-2">Utilizamos suas informações para:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Disponibilizar suas reservas no Porto 2 Life</li>
                <li>Facilitar a comunicação entre hóspedes e proprietários, permitindo que usuários logados visualizem dados de contato (nome, email e telefone) uns dos outros</li>
                <li>Enviar confirmações, lembretes e atualizações de reservas</li>
                <li>Fornecer suporte ao cliente</li>
                <li>Melhorar nossos serviços e experiência do usuário</li>
                <li>Enviar ofertas e promoções (com seu consentimento)</li>
                <li>Prevenir fraudes e garantir a segurança da plataforma</li>
                <li>Cumprir obrigações legais e regulatórias</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">4. Compartilhamento de Informações</h2>
              <p className="text-muted-foreground mb-3">
                Para facilitar a comunicação e a realização de reservas entre usuários da plataforma, compartilhamos algumas informações pessoais de forma controlada e segura:
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">📋 Compartilhamento entre Usuários Logados</h3>
                <p className="text-blue-800 text-sm mb-2">
                  <strong>Dados compartilhados:</strong> Nome, Email e Telefone
                </p>
                <p className="text-blue-800 text-sm mb-2">
                  <strong>Quando compartilhados:</strong> Apenas quando você está logado no sistema e interage com outros usuários (proprietários ou hóspedes) através de:
                </p>
                <ul className="list-disc pl-6 text-blue-800 text-sm space-y-1">
                  <li>Solicitações de reserva</li>
                  <li>Mensagens e conversas na plataforma</li>
                  <li>Confirmação de reservas</li>
                </ul>
                <p className="text-blue-800 text-sm mt-2">
                  <strong>⚠️ Importante:</strong> Esses dados são disponibilizados <strong>apenas para outros usuários que também estão logados no sistema</strong>, garantindo que apenas membros ativos da plataforma tenham acesso às informações de contato necessárias para comunicação.
                </p>
                <p className="text-blue-800 text-sm mt-2">
                  Este compartilhamento é essencial para permitir que proprietários e hóspedes se comuniquem diretamente, facilitando o processo de reserva e melhorando a experiência de todos os usuários.
                </p>
              </div>

              <p className="text-muted-foreground mb-2">
                Também podemos compartilhar suas informações com:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Porto 2 Life Resort:</strong> Informações para acesso, segurança e gestão de reservas</li>
                <li><strong>Autoridades:</strong> Quando exigido por lei ou ordem judicial</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                <strong>Não vendemos suas informações pessoais a terceiros.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">5. Segurança dos Dados</h2>
              <p className="text-muted-foreground mb-2">
                Implementamos medidas de segurança técnicas e organizacionais:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Criptografia SSL/TLS para transmissão de dados</li>
                <li>Armazenamento seguro em servidores protegidos</li>
                <li>Controles de acesso rigorosos</li>
                <li>Monitoramento constante de atividades suspeitas</li>
                <li>Auditorias de segurança regulares</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">6. Seus Direitos (LGPD)</h2>
              <p className="text-muted-foreground mb-2">
                De acordo com a LGPD, você tem direito a:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Confirmar a existência de tratamento de dados</li>
                <li>Acessar seus dados pessoais</li>
                <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
                <li>Solicitar a anonimização, bloqueio ou eliminação de dados</li>
                <li>Solicitar a portabilidade dos dados</li>
                <li>Revogar o consentimento</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                Para exercer esses direitos, entre em contato através do email:{" "}
                <a 
                  href="mailto:livestaybrasil@gmail.com" 
                  className="text-primary hover:underline font-medium"
                >
                  livestaybrasil@gmail.com
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">7. Cookies</h2>
              <p className="text-muted-foreground">
                Utilizamos cookies para manter você conectado, lembrar preferências, analisar o uso 
                do site e personalizar conteúdo. Você pode gerenciar cookies através das configurações 
                do seu navegador.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">8. Retenção de Dados</h2>
              <p className="text-muted-foreground">
                Mantemos suas informações pelo tempo necessário para fornecer nossos serviços, cumprir 
                obrigações legais (ex: dados fiscais por 5 anos), resolver disputas e fazer cumprir 
                nossos acordos.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">9. Alterações nesta Política</h2>
              <p className="text-muted-foreground">
                Podemos atualizar esta política periodicamente. Notificaremos você sobre alterações 
                significativas através de email ou aviso no site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">10. Encarregado de Proteção de Dados (DPO)</h2>
              <p className="text-muted-foreground mb-2">
                Para questões relacionadas à proteção de dados, entre em contato através do email:
              </p>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>Email:</strong>{" "}
                  <a 
                    href="mailto:livestaybrasil@gmail.com" 
                    className="text-primary hover:underline font-medium"
                  >
                    livestaybrasil@gmail.com
                  </a>
                </p>
                <p className="text-muted-foreground mt-2 text-sm">
                  Este é o email de contato único para hóspedes e proprietários, incluindo questões sobre privacidade e proteção de dados pessoais conforme a LGPD.
                </p>
              </div>
            </section>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
